import { memo, useCallback } from 'react';
import { z } from 'zod';
import {
    Button,
    Form,
    useForm,
    zodResolver,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
} from '@flote/ui';
import { tsr } from '@flote/app-core';
import type { DeliveryDestination } from '@flote/types/delivery';
import { CreateDeliveryDestinationSchema } from '@flote/types/delivery';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useModal } from '../../lib/modal';
import { useQueryClient } from '@tanstack/react-query';

const formSchema = CreateDeliveryDestinationSchema;
type FormValues = z.infer<typeof formSchema>;

interface AddressModalProps {
    onSave: (destination: DeliveryDestination) => void;
}

export const AddressModal = memo<AddressModalProps>(({ onSave }) => {
    const { closeModal } = useModal();
    const queryClient = useQueryClient();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            address1: '',
            address2: '',
            city: '',
            province: '',
            postalCode: '',
        },
    });

    const { mutateAsync, isPending } = tsr.checkout.createDeliveryDestination.useMutation();

    const handleSave = useCallback(
        async (data: FormValues) => {
            try {
                const response = await mutateAsync({ body: data });

                if (response.status === 201) {
                    toast.success('Address added successfully');
                    onSave(response.body);
                    queryClient.invalidateQueries({ queryKey: ['delivery-destinations'] });
                    form.reset();
                    closeModal();
                }
            } catch (error) {
                console.error('Error creating address', error);
                toast.error('Unable to add address', {
                    description: error instanceof Error ? error.message : 'An unexpected error occurred',
                });
            }
        },
        [mutateAsync, onSave, closeModal, form, queryClient],
    );

    const handleCancel = useCallback(() => {
        form.reset();
        closeModal();
    }, [form, closeModal]);

    return (
        <Dialog open onOpenChange={open => !open && handleCancel()}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Add New Address</DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Location Name</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="e.g. Main Store, Bozeman Warehouse" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="address1"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Street Address</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="123 Main St" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="address2"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Apartment, suite, etc. <span className="text-muted-foreground">(optional)</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Apt 4B" value={field.value || ''} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="city"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>City</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Bozeman" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="province"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>State</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="MT" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="postalCode"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>ZIP Code</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="59715" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="flex items-end">
                                    <div className="w-full">
                                        <FormLabel className="text-sm font-medium">Country</FormLabel>
                                        <div className="mt-2">
                                            <Input value="US" disabled className="bg-muted" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    className="flex-1"
                                    disabled={isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1"
                                    disabled={form.formState.isSubmitting || isPending}
                                >
                                    {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Address'}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </DialogContent>
        </Dialog>
    );
});

AddressModal.displayName = 'AddressModal';
